import { Component, OnDestroy, inject, model, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService, ENVIRONMENT, ModalService, UserService } from '@cfp/core';
import { LoginCard } from '@cfp/shared/ui/login';
import { ContactModal } from '@cfp/shared/ui/modals';
import {
  Alert,
  Button,
  FormGroupActionsDirective,
  FormGroupComponent,
  InputPassword,
  InputText,
  Link,
  ValidationFeedback
} from '@cfp/ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoginLoadingSpinner, LoginMessages, LoginSso } from '../../components';
import { LoginFormService } from '../../services';

@Component({
  selector: 'cfp-login-page',
  templateUrl: './login-page.html',
  styleUrls: ['./login-page.scss'],
  imports: [
    LoginCard,
    LoginLoadingSpinner,
    TranslateModule,
    LoginMessages,
    RouterLink,
    ReactiveFormsModule,
    FormGroupComponent,
    InputText,
    Link,
    InputPassword,
    Button,
    LoginSso,
    FormGroupActionsDirective,
    ValidationFeedback,
    Alert
  ]
})
export class LoginPage implements OnDestroy {
  public readonly isLoading = model(false);

  protected readonly form: FormGroup;
  protected readonly basePath: string = '/login';

  protected readonly changePassword = signal(false);
  protected readonly loginAttempts = signal<{ [username: string]: number }>({});
  protected readonly warningMessages = signal<string[]>([]);
  protected readonly interpolateParams = signal<Record<string, any>>({});
  protected readonly errorMessages = signal<string[]>([]);
  protected readonly loginMessagesDataTest = signal<string | undefined>(undefined);
  protected readonly showLoginAttemptsWarning = signal<boolean>(false);

  protected readonly environment = inject(ENVIRONMENT);
  private readonly loginFormService = inject(LoginFormService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);

  protected readonly loginAllowSelfOnboarding = this.environment.loginAllowSelfOnboarding === true;

  constructor() {
    this.form = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
    const basePath = this.route.snapshot.data?.basePath;
    if (basePath) {
      this.basePath = basePath;
    }
  }

  public ngOnDestroy(): void {
    if (this.changePassword()) {
      this.authService.logout();
    }
  }

  protected closeWindow(): void {
    window.close();
  }

  protected submitForm(): boolean {
    this.changePassword.set(false);
    this.loginMessagesDataTest.set(undefined);

    if (!this.validateLoginAttempts(this.form.value.username) || !this.validateForm()) {
      return false;
    }

    this.isLoading.set(true);

    this.authService.login(this.form.value.username, this.form.value.password).subscribe({
      next: () => {
        this.authService.goToApp(this.route.snapshot);
      },
      error: (error) => {
        this.isLoading.set(false);
        this.errorMessages.set([]);
        this.warningMessages.set([]);
        this.loginMessagesDataTest.set(undefined);
        this.interpolateParams.set({});
        this.userService.changePasswordUserName.set(undefined);
        this.changePassword.set(false);

        if (error.message === 'changePassword') {
          this.warningMessages.set(['login.error.explainChange']);
          this.loginMessagesDataTest.set('warningChangePassword');
          this.userService.changePasswordUserName.set(this.form.value.username);
          this.changePassword.set(true);
        } else if (error.error && error.error.code == 'PASSWORD_EXPIRED') {
          this.warningMessages.set(['login.error.passwordExpired']);
          this.loginMessagesDataTest.set('warningChangePassword');
          this.interpolateParams.set({
            forgot: this.translateService.instant('login.forgot')
          });
        } else if (error.status == 504 || error.status == 503) {
          this.errorMessages.set(['login.error.serviceUnavailable']);
          this.loginMessagesDataTest.set('errorServiceUnavailable');
        } else {
          this.errorMessages.set(['login.error.passwordNotCorrect', 'login.error.passwordNotCorrectExpires']);
          this.loginMessagesDataTest.set('errorPasswordNotCorrect');
          this.interpolateParams.set({
            forgot: this.translateService.instant('login.forgot')
          });
        }
      }
    });

    return true;
  }

  protected validateForm(): boolean {
    if (this.form.status === 'INVALID') {
      this.errorMessages.set(this.loginFormService.transformErrorMessages(this.form));
      return false;
    }

    this.errorMessages.set([]);
    return true;
  }

  /**
   * If user tries more than 3 times a warning will be emitted
   */
  protected validateLoginAttempts(username: string): boolean {
    this.showLoginAttemptsWarning.set(false);

    if (this.loginAttempts[username]) {
      this.loginAttempts[username]++;
    } else {
      this.loginAttempts[username] = 1;
    }
    // validate login is done before login, so we check on 4 attempts (instead of 3)
    if (this.loginAttempts[username] >= 4) {
      this.showLoginAttemptsWarning.set(true);
      return false;
    }

    this.warningMessages.set([]);
    return true;
  }

  protected contactSupport(): void {
    this.modalService.show(ContactModal, {
      initialState: {
        showMessage: true,
        subject: 'Unlock account',
        title: 'login.error.contactSupport.title',
        intro: 'login.error.contactSupport.intro'
      }
    });
  }

  protected routePasswordChange(): void {
    this.changePassword.set(false);
    this.router.navigateByUrl('/login/auth/change');
  }
}
